/**
 * The WebSocket protocol, validated at the boundary.
 *
 * The predecessor cast raw socket JSON straight to a domain type
 * (`const msg: Message = data`) and fanned it into five stores from a 90-line
 * type switch. Nothing checked that the server sent what the client assumed.
 */

import { z } from 'zod';

const scalars = z.record(z.string(), z.unknown());
const telemetryCache = z.record(z.string(), z.record(z.string(), z.unknown()));

/** Full state for every drone the client may see. Sent once on connect. */
export const snapshotMessage = z.object({
  type: z.literal('snapshot'),
  drones: z.array(
    z.object({
      droneId: z.string(),
      callsign: z.string(),
      autopilotSysId: z.number().nullable(),
      state: z.enum(['disconnected', 'connecting', 'connected', 'stale']),
      scalars,
      telemetry: telemetryCache,
      link: z.string(),
      simulated: z.boolean(),
      lastMessageAt: z.number().nullable(),
      // Declared so the parse keeps it: zod drops keys it was not told about,
      // and without this every other team's aircraft would arrive looking like
      // one of the caller's own, with command buttons the server will refuse.
      access: z.enum(['full', 'map']).optional(),
    }),
  ),
});

/** Coalesced per-drone deltas, one batch per broadcast tick. */
export const deltaMessage = z.object({
  type: z.literal('delta'),
  deltas: z.array(
    z.object({
      droneId: z.string(),
      scalars,
      telemetry: telemetryCache,
      observedAt: z.number(),
    }),
  ),
});

/** A drone appeared, disappeared, or changed connection state. */
export const rosterMessage = z.object({
  type: z.literal('roster'),
  added: z.array(z.string()).default([]),
  removed: z.array(z.string()).default([]),
});

export const alertMessage = z.object({
  type: z.literal('alert'),
  severity: z.enum(['info', 'warning', 'critical']),
  droneId: z.string().nullable(),
  message: z.string(),
  at: z.number(),
});

/** The server is telling the client its access set changed. Re-fetch. */
export const accessChangedMessage = z.object({
  type: z.literal('access_changed'),
});

/**
 * One line the autopilot said (a MAVLink STATUSTEXT), as its own frame.
 *
 * A discrete event, never coalesced into a delta: an event merged away is a
 * warning the operator never saw. `severity` is the raw MAV_SEVERITY (0=most
 * severe .. 7=least); `level` is its name, carried so the UI colours it without
 * re-deriving the mapping the server already applied.
 */
export const autopilotMessage = z.object({
  type: z.literal('autopilot_message'),
  message: z.object({
    droneId: z.string(),
    severity: z.number(),
    level: z.string(),
    text: z.string(),
    timestamp: z.number(),
  }),
});

/**
 * One operator notification: a link that died, a battery past a line, a fence
 * crossed, an engagement that ended.
 *
 * Pushed live, but the panel reads from `/api/notifications` rather than from
 * what it happened to receive — every notification is written to the server's
 * store before it is published, so a client that was not connected, or whose
 * outbox dropped a frame under backpressure, still sees it.
 *
 * `metadata` is deliberately unvalidated beyond being an object. It carries the
 * per-kind payload (an engagement assessment, a link diagnosis, a zone id), and
 * pinning its shape here would mean this file had to change every time a kind
 * gained a field. What the UI must NOT do is render an estimate as a fact: an
 * assessment carries `confirmed: false` and `estimated: true` as fields for
 * exactly that reason.
 */
export const notificationMessage = z.object({
  type: z.literal('notification'),
  notification: z.object({
    kind: z.string(),
    severity: z.enum(['info', 'notice', 'warning', 'error', 'critical']),
    droneId: z.string().nullable(),
    title: z.string(),
    detail: z.string(),
    at: z.number(),
    dedupKey: z.string(),
    metadata: z.record(z.string(), z.unknown()).default({}),
  }),
});

const waypointWire = z.object({
  lat: z.number(),
  lon: z.number(),
  alt: z.number(),
  command: z.number(),
  frame: z.number(),
  param1: z.number(),
  param2: z.number(),
  param3: z.number(),
  param4: z.number(),
  autocontinue: z.boolean(),
});

/**
 * A carrier plan changed: drones went aboard, launched, landed, or were
 * recovered. Pushed by the server's executor, which is what makes a launch
 * visible the moment it happens rather than on somebody's next poll. `plan` is
 * null when the plan was removed.
 */
export const carrierMessage = z.object({
  type: z.literal('carrier'),
  carrierId: z.string(),
  plan: z
    .object({
      carrierId: z.string(),
      ownerId: z.string(),
      ownerUsername: z.string(),
      embarked: z.array(
        z.object({
          droneId: z.string(),
          deployIndex: z.number(),
          deployLat: z.number(),
          deployLon: z.number(),
          launchAltM: z.number(),
          mission: z.array(waypointWire),
        }),
      ),
      status: z.record(
        z.string(),
        z.object({
          phase: z.enum(['aboard', 'launching', 'airborne', 'landed']),
          message: z.string(),
          updatedAt: z.number(),
        }),
      ),
      fired: z.array(z.number()),
      updatedAt: z.number(),
    })
    .nullable(),
});

export const serverMessage = z.discriminatedUnion('type', [
  snapshotMessage,
  deltaMessage,
  rosterMessage,
  alertMessage,
  accessChangedMessage,
  autopilotMessage,
  notificationMessage,
  carrierMessage,
]);

export type ServerMessage = z.infer<typeof serverMessage>;
export type SnapshotMessage = z.infer<typeof snapshotMessage>;
export type DeltaMessage = z.infer<typeof deltaMessage>;

/**
 * Parse an inbound frame, returning null on anything unrecognised.
 *
 * Returning null rather than throwing keeps one malformed frame from tearing
 * down a socket that is otherwise delivering telemetry fine.
 */
export function parseServerMessage(raw: unknown): ServerMessage | null {
  const result = serverMessage.safeParse(raw);
  return result.success ? result.data : null;
}
